import * as THREE from 'three';

import { CoordinateManager } from '../../coordinate-manager';
import { ProjectGeodataManager } from '../../project-geodata-manager';
import { GlobalMercator } from '../../utilities/projections/global-mercator';
import { getCogCorners } from '../cog-asset/cog-projection';

/**
 * Resolves the ortho tile scene origo in project coordinates.
 * XYZ: the zero-level tile bounds (SW corner in WGS84) converted to project CRS.
 * COG: the SW corner of the image in project CRS.
 * The height comes from the orthotile model's bounding box.
 * Used by the ortho scene so it can set CoordinateManager without depending on OrthotileManager.
 */
export function resolveOrthoTileOrigo(projection: string): THREE.Vector3 {
  const orthoTileGeodata = ProjectGeodataManager.instance().getActivOrthotileGeodata();

  if (orthoTileGeodata?.format === 'COG') {
    const corners = getCogCorners(orthoTileGeodata.source, projection).project;
    return new THREE.Vector3(
      Math.min(...corners.map(([x]) => x)),
      Math.min(...corners.map(([, y]) => y)),
      orthoTileGeodata.model.boundingBoxMin?.[2] ?? 0,
    );
  }

  const model = orthoTileGeodata?.model;
  if (!model?.levels?.length) {
    return new THREE.Vector3();
  }

  const levels = model.levels;
  const zeroLevel = levels[0];
  const globalMercator = new GlobalMercator(model.tileSize);
  const lowBounds = globalMercator.tileLatLonBounds(
    zeroLevel.columnMin,
    zeroLevel.rowMax,
    zeroLevel.zoom,
  );

  const projectCoords = CoordinateManager.getInstance().convertCoords(
    lowBounds.west,
    lowBounds.south,
    'wgs84',
    projection,
  );
  return new THREE.Vector3(projectCoords.x, projectCoords.y, model.boundingBoxMin[2]);
}
